<template>
  <div class="flex flex-col gap-2">
    <p class="text-primary">{{ props.label }}</p>
    <input
      :inputmode="isVirtualKeyboardEnabled ? 'text' : 'none'"
      ref="input"
      v-model="formattedData"
      :type="isVirtualKeyboardEnabled ? type : 'text'"
      :disabled="props.disabled"
      @keyup.enter="onEnterUp"
      :class="inputClass"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, type InputTypeHTMLAttribute } from 'vue';

type InputTheme = 'primary' | 'secondary'

type Props = {
  label?: string;
  type?: InputTypeHTMLAttribute;
  disabled?: boolean;
  theme?: InputTheme;
  upperCase?: boolean;
  modelValue: string;
  align?: 'left' | 'center' | 'right';
  isVirtualKeyboardEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  theme: 'primary',
  upperCase: false,
  align: 'left',
  isVirtualKeyboardEnabled: false
})

const emit = defineEmits(['enter-key-up', 'update:modelValue'])

// Ref local que representa o valor interno do input
const data = ref(props.modelValue || '')

const inputClass = computed(() => {
  const inputClassMap: Record<InputTheme, string> = {
    primary: 'input-primary',
    secondary: 'input-secondary'
  }

  const alignClass =
    {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right'
    }[props.align] + ' '

  const defaultClasses = 'input '

  return defaultClasses + alignClass + inputClassMap[props.theme]
})

const input = ref<HTMLInputElement | null>(null);

function focus() {
  input.value?.focus()
}

function blur() {
  input.value?.blur()
}

function onEnterUp(event: KeyboardEvent) {
  emit('enter-key-up', event)
}

const formattedData = computed({
  get() {
    return props.upperCase ? data.value.toUpperCase() : data.value
  },
  set(value: string) {
    data.value = props.upperCase ? value.toUpperCase() : value
    emit('update:modelValue', data.value)
  }
})

watch(
  () => props.modelValue,
  (newVal) => {
    return (data.value = newVal)
  }
)

defineExpose({
  focus,
  blur
})
</script>
